import { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useCurrentClient } from '@mysten/dapp-kit-react'
import { toast } from 'react-toastify'
import { Price } from '@kunalabs-io/kai'

import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { IconInfo } from 'components/Icons/InfoIcon'
import { Button } from 'components/Button/Button'
import { DepositDialog } from './DepositDialog'
import { IconClose } from 'components/Icons/IconClose'
import { ClaimInfoSubmit, ReductionInfoSubmit } from './types'
import { useExecute } from 'utils/useExecute'
import { CurrentPrice } from './CurrentPrice'
import { ExistingPositionBreakdown } from './ExistingPositionBreakdown'
import {
  ellipsizeTokenAddress,
  getDisplayTimestamp,
  getHealthColor,
  getPositionStatusBgColor,
  getPositionStatusColor,
  getPositionStatusLabel,
} from 'utils/helpers'
import { SLIPPAGE } from 'utils/const'
import {
  getAprValue,
  getHealthValue,
  getInRangeValue,
  getPnlValue,
  getPositionRangeMax,
  getPositionRangeMin,
} from 'modules/MyPositions/utils'
import { IconCopy } from 'components/Icons/IconCopy'
import { useRouter } from 'contexts/SwapRouterContext'
import LiquidityChartRangeInput from 'components/LiquidityChart'
import { Bound } from 'components/LiquidityChart/types'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'
import { usePositionMetrics } from './Analytics/hooks/usePositionMetrics'
import { ClaimDialog } from './ClaimDialog'
import { PendingFeesRewardsTotal, PendingFeesRewardsTotalClosed } from './PendingFeesRewardsTotal'
import { useRewardsFeesInfo } from './hooks/useRewardFeeInfos'
import { Banner } from 'components/Banner/Banner'
import { PairInfo } from 'utils/pair-info'
import { WithdrawDialog } from './WithdrawDialog/WithdrawDialog'
import { usePostHogEntityViewed } from 'utils/usePostHogEntityViewed'
import { usePostHogEntityAction } from 'utils/usePostHogEntityAction'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { LP_POSITION_CLAIM, LP_POSITION_COMPOUND, LP_POSITION_WITHDRAW } from 'utils/sentry-utils'
import { useUserPositionDisplayInfo } from 'utils/userUserPositionDisplayInfos'
import { usePreProcessedPositionUpdate } from './hooks/usePreProcessedPositionUpdate'

const PositionOverviewContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: auto;
  margin-top: 24px;
  align-items: stretch;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    // padding: 0 20px;
  }
`

const LeftContainer = styled.div`
  width: 75%;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }
`
const RightContainer = styled.div`
  width: 25%;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }
`

const ContentContainer = styled.div`
  padding: 16px;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};

  // @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
  //   padding: 24px;
  // }
`

const InfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    gap: 16px;
  }
`

const InfoItem = styled.div`
  text-align: left;
  flex: 1 1 16.5%;
  box-sizing: border-box;
  // border: 1px solid red;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    min-width: 140px;
  }
`

const SectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  svg {
    margin-left: 6px;
    path {
      stroke: ${p => p.theme.colors.text.secondary};
    }
  }
`

const TooltipSectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const SectionContent = styled.div`
  display: flex;
  padding: 20px 16px;
  align-items: center;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const Gap = styled.div`
  height: 40px;
`

const SectionGap = styled.div`
  height: 32px;
`

const MinMaxPriceLabelContainer = styled.div`
  display: flex;
  gap: 24px;
  & > div {
    width: 50%;
    justify-content: flex-start;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  & > button {
    width: 50%;
  }
`

const Status = styled.div<{ $type: 'active' | 'inactive' | 'closed' }>`
  margin-top: 8px;
  height: 26px;
  width: 70px;
  border-radius: 4px;
  color: ${p => getPositionStatusColor(p.$type, p.theme)};
  background-color: ${p => getPositionStatusBgColor(p.$type, p.theme)};
  ${p =>
    p.$type === 'inactive' &&
    `
    border: 1px solid ${p.theme.colors.text.primary};
    box-sizing: border-box;
  `}
  font-size: ${p => p.theme.typography.size.Caption1}px;
  font-weight ${p => p.theme.typography.weight.Bold};
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: capitalize;
`

const PositionId = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
  & > div > svg > g > path {
    stroke: ${p => p.theme.colors.text.secondary};
  }
`

interface Props {
  positionInfo: PositionInfo
  onCloseClick: () => void
  onReduceSuccess: () => void
  onReduceProcessed?: () => void
  onDepositSuccess: () => void
  onDepositProcessed?: () => void
  refetchPositionDetails: () => Promise<void>
  pairInfo: PairInfo<string>
}

export const PositionOverview = ({
  onCloseClick,
  positionInfo,
  onReduceSuccess,
  onReduceProcessed,
  onDepositSuccess: onDepositSuccessFinal,
  onDepositProcessed,
  refetchPositionDetails,
  pairInfo,
}: Props) => {
  const {
    result: rewardsFeesInfos,
    isLoading: isLoadingRewardsFees,
    error: rewardsFeesInfoError,
    refetch: refetchRewardsFeeInfo,
  } = useRewardsFeesInfo(positionInfo)

  const { capture } = usePostHogEntityAction()
  const preProcessedPositionUpdate = usePreProcessedPositionUpdate()

  const [isLoadingWithdraw, setIsLoadingWithdraw] = useState(false)

  const [isLoadingCompound, setIsLoadingCompound] = useState(false)
  const [isLoadingClaim, setIsLoadingClaim] = useState(false)

  const { router } = useRouter()

  const isMobile = useIsMobile()
  const theme = useTheme()

  const [reduceModalPosition, setReduceModalPosition] = useState<PositionInfo | undefined>(undefined)

  const [isOpenDepositModal, setIsOpenDepositModal] = useState(false)
  const [isOpenWithdrawModal, setIsOpenWithdrawModal] = useState(false)
  const [isOpenClaimDialog, setIsOpenClaimDialog] = useState(false)

  const openDepositDialog = useCallback(() => setIsOpenDepositModal(true), [])
  const closeDepositDialog = useCallback(() => setIsOpenDepositModal(false), [])

  const openWithdrawDialog = useCallback(() => setIsOpenWithdrawModal(true), [])
  const closeWithdrawDialog = useCallback(() => setIsOpenWithdrawModal(false), [])

  const openClaimDialog = useCallback(() => setIsOpenClaimDialog(true), [])
  const closeClaimDialog = useCallback(() => {
    setIsOpenClaimDialog(false)
    setIsLoadingClaim(false)
    setIsLoadingCompound(false)
  }, [])

  const { positionDisplayInfo, isLoading } = useUserPositionDisplayInfo(positionInfo)
  const pool = positionDisplayInfo?.pool
  const configInfo = positionInfo.lastPosition.configInfo
  const isReversedPair = configInfo.isReversedPair
  const supplyPoolX = positionDisplayInfo?.supplyPoolX
  const supplyPoolY = positionDisplayInfo?.supplyPoolY

  usePostHogEntityViewed('lp:position_details_view', {
    entityId: positionInfo.positionId,
    entityName: configInfo.name || '',
  })

  const { data: positionMetrics, isLoading: isLoadingPositionMetrics } = usePositionMetrics(positionInfo.positionId)

  const handleWithdrawClick = useCallback(() => {
    setReduceModalPosition(positionInfo)
    openWithdrawDialog()
  }, [openWithdrawDialog, positionInfo])

  const execute = useExecute()
  const client = useCurrentClient()
  const walletAddress = useCurrentWalletAddress()
  const isConnected = !!walletAddress

  useEffect(() => {
    if (!isConnected) {
      onCloseClick()
    }
  }, [isConnected, onCloseClick])

  const onDepositSuccess = useCallback(() => {
    closeDepositDialog()
    onDepositSuccessFinal()
  }, [closeDepositDialog, onDepositSuccessFinal])

  const onClaim = useCallback(
    async (args: ClaimInfoSubmit) => {
      if (!router || !client || !walletAddress || isLoadingClaim || isLoadingCompound) {
        return
      }

      setIsLoadingClaim(true)

      void execute(
        () =>
          positionInfo.lastPosition.withdrawAllRewardsConvertAndTransfer(
            client,
            router,
            {
              positionCapId: positionInfo.positionCapId,
              convertRewardsTo: args.convertRewardsTo,
              slippage: SLIPPAGE,
            },
            walletAddress
          ),
        {
          transactionType: LP_POSITION_CLAIM,
          sentryContext: {
            positionId: positionDisplayInfo?.position.id || '',
            walletAddress: walletAddress || '',
            convertRewardsTo: args.convertRewardsTo?.typeName || '',
            positionCapId: positionInfo.positionCapId,
            slippage: SLIPPAGE,
          },
          onSuccess: async () => {
            await Promise.all([refetchRewardsFeeInfo(), preProcessedPositionUpdate(positionInfo.positionId)])
            setIsLoadingClaim(false)
            closeClaimDialog()
          },
          onProcessed: async () => {
            await refetchPositionDetails()
          },
          onError: () => {
            setIsLoadingClaim(false)
          },
        }
      )
    },
    [
      refetchRewardsFeeInfo,
      refetchPositionDetails,
      router,
      client,
      walletAddress,
      isLoadingClaim,
      isLoadingCompound,
      positionInfo.lastPosition,
      positionInfo.positionCapId,
      positionInfo.positionId,
      execute,
      closeClaimDialog,
      positionDisplayInfo?.position.id,
      preProcessedPositionUpdate,
    ]
  )

  const onCompound = useCallback(async () => {
    if (!router || !client || !walletAddress || !pool || isLoadingClaim || isLoadingCompound) {
      return
    }

    setIsLoadingCompound(true)

    void execute(
      () =>
        positionInfo.lastPosition.compound(
          client,
          {
            pool,
            positionCapId: positionInfo.positionCapId,
            slippage: SLIPPAGE,
          },
          walletAddress
        ),
      {
        transactionType: LP_POSITION_COMPOUND,
        sentryContext: {
          positionId: positionDisplayInfo?.position.id || '',
          walletAddress: walletAddress || '',
          poolId: pool.id,
          positionCapId: positionInfo.positionCapId,
          slippage: SLIPPAGE,
        },
        onSuccess: async () => {
          await Promise.all([refetchRewardsFeeInfo(), preProcessedPositionUpdate(positionInfo.positionId)])
          setIsLoadingCompound(false)
          closeClaimDialog()
        },
        onProcessed: async () => {
          await refetchPositionDetails()
        },
        onError: () => {
          setIsLoadingCompound(false)
        },
      }
    )
  }, [
    refetchPositionDetails,
    refetchRewardsFeeInfo,
    router,
    client,
    walletAddress,
    pool,
    isLoadingClaim,
    isLoadingCompound,
    execute,
    positionInfo.lastPosition,
    positionInfo.positionCapId,
    positionInfo.positionId,
    positionDisplayInfo?.position.id,
    closeClaimDialog,
    preProcessedPositionUpdate,
  ])

  const onReduceConfirm = useCallback(
    async (reductionInfo?: ReductionInfoSubmit, priceX?: Price<string, string>, priceY?: Price<string, string>) => {
      if (
        !router ||
        !reduceModalPosition ||
        !reductionInfo ||
        !pool ||
        !configInfo ||
        !walletAddress ||
        isLoadingWithdraw
      ) {
        return
      }
      setIsLoadingWithdraw(true)
      const position = reduceModalPosition.lastPosition

      const positionCapId = positionInfo.positionCapId
      if (!positionCapId) {
        throw new Error(`Position cap ID not found for ${position.id}`)
      }

      void execute(
        () =>
          position.reduceAndMaybeDelete(
            client,
            router,
            {
              factor: reductionInfo.factor,
              positionCapId,
              convertRewardsTo: reductionInfo.convertRewardsTo,
              routerArgs:
                reductionInfo.swapMethod === 'exact-out'
                  ? {
                      slippage: reductionInfo.slippage,
                      swapMethod: reductionInfo.swapMethod,
                      protocolDenylist: reductionInfo.protocolDenyList,
                    }
                  : {
                      slippage: reductionInfo.slippage,
                      overestimationFactor: reductionInfo.overestimation,
                      swapMethod: reductionInfo.swapMethod,
                      protocolDenylist: reductionInfo.protocolDenyList,
                    },
            },
            walletAddress
          ),
        {
          transactionType: LP_POSITION_WITHDRAW,
          sentryContext: {
            positionId: positionInfo.positionId,
            walletAddress: walletAddress || '',
            factor: reductionInfo.factor.toString(),
            finalX: reductionInfo.finalX.toString(),
            finalY: reductionInfo.finalY.toString(),
            positionCapId,
            slippage: SLIPPAGE,
            convertRewardsTo: reductionInfo.convertRewardsTo?.typeName || '',
          },
          onSuccess: async () => {
            await preProcessedPositionUpdate(
              positionInfo.positionId,
              reductionInfo.factor.eq(1) ? { deleted: true } : undefined
            )

            capture('lp:withdraw', {
              entity_id: positionInfo.positionId,
              config_id: configInfo.configId || '',
              config_name: configInfo.name || '',
              equity_usd:
                priceX && priceY
                  ? Math.round(
                      (reductionInfo.finalX.toNumber() * priceX.human.toNumber() +
                        reductionInfo.finalY.toNumber() * priceY.human.toNumber()) *
                        100
                    ) / 100
                  : null,
            })
            setIsLoadingWithdraw(false)
            setReduceModalPosition(undefined)
            onReduceSuccess()
          },
          onProcessed: async () => {
            onReduceProcessed?.()
          },
          onError: () => {
            setIsLoadingWithdraw(false)
          },
        }
      )
    },
    [
      positionInfo.positionCapId,
      positionInfo.positionId,
      reduceModalPosition,
      pool,
      configInfo,
      walletAddress,
      client,
      execute,
      onReduceSuccess,
      onReduceProcessed,
      capture,
      isLoadingWithdraw,
      router,
      preProcessedPositionUpdate,
    ]
  )

  const handleCopyClick = useCallback(
    async (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!positionDisplayInfo) {
        return
      }

      event.stopPropagation()
      await navigator.clipboard.writeText(positionDisplayInfo.position.id)
      toast.success('Position ID copied')
    },
    [positionDisplayInfo]
  )

  const isFrozen = useMemo(() => {
    return pairInfo.frozenConfigInfos.some(ci => ci.configId === positionDisplayInfo?.position.configInfo.configId)
  }, [positionDisplayInfo, pairInfo])

  const isWithdrawOnly = useMemo(() => {
    return pairInfo.withdrawOnlyConfigs.some(ci => ci.configId === positionDisplayInfo?.position.configInfo.configId)
  }, [positionDisplayInfo, pairInfo])

  return (
    !isLoading &&
    positionDisplayInfo &&
    pool &&
    configInfo &&
    supplyPoolX &&
    supplyPoolY && (
      <>
        {(isWithdrawOnly || isFrozen) && positionDisplayInfo.status === 'active' && (
          <Banner style={{ marginTop: 24 }}>
            <Typography variant={isMobile ? 'caption2' : 'body'} fontWeight="semiBold">
              This LP position is currently in withdraw-only mode. If you have any questions, please visit our{' '}
              <a href="https://discord.gg/XV2xYZjReX" target="_blank" rel="noreferrer">
                Discord
              </a>
              .
            </Typography>
          </Banner>
        )}

        <PositionOverviewContainer>
          <LeftContainer>
            <ContentContainer>
              <Typography
                variant={isMobile ? 'description' : 'label1'}
                fontWeight="semiBold"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                Position Overview
                <div style={{ marginLeft: 'auto', display: 'flex', cursor: 'pointer' }} onClick={onCloseClick}>
                  <IconClose />
                </div>
              </Typography>
              <PositionId>
                <Typography variant="body" color="secondary">
                  ID: {ellipsizeTokenAddress(positionDisplayInfo.position.id)}
                </Typography>
                <div style={{ display: 'flex', marginLeft: 8, cursor: 'pointer' }} onClick={handleCopyClick}>
                  <IconCopy />
                </div>
              </PositionId>
              <Gap />
              <TooltipSectionLabel variant="label2" color="secondary" fontWeight="medium">
                Protocol
                <Tooltip content="The DEX where this LP position is deployed." position="top">
                  <IconInfo width={16} height={16} />
                </Tooltip>
              </TooltipSectionLabel>
              <SectionContent>
                <Typography
                  variant={isMobile ? 'description' : 'label1'}
                  fontWeight="bold"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <img src={positionDisplayInfo.protocolImgSrc} style={{ width: 24, height: 24, marginRight: 8 }} />
                  {positionDisplayInfo.poolName}
                </Typography>
              </SectionContent>
              <SectionGap />
              <TooltipSectionLabel variant="label2" color="secondary" fontWeight="medium">
                Info
                <Tooltip content="Additional details about the selected LP position." position="top">
                  <IconInfo width={16} height={16} />
                </Tooltip>
              </TooltipSectionLabel>
              <InfoContainer>
                <InfoItem style={{ flexDirection: 'column', display: 'flex', alignItems: 'start' }}>
                  <Typography variant="description" color="secondary" fontWeight="medium">
                    Creation date
                  </Typography>
                  <div style={{ marginTop: 8 }}>
                    {positionDisplayInfo.creationTimestampMs
                      ? getDisplayTimestamp(positionDisplayInfo.creationTimestampMs)
                      : '-'}
                  </div>
                </InfoItem>
                <InfoItem style={{ flexDirection: 'column', display: 'flex', alignItems: 'start' }}>
                  <Typography variant="description" color="secondary" fontWeight="medium">
                    Status
                  </Typography>
                  <Status $type={positionDisplayInfo.status}>
                    {getPositionStatusLabel(positionDisplayInfo.status)}
                  </Status>
                </InfoItem>
                <InfoItem>
                  <Typography variant="description" color="secondary" fontWeight="medium">
                    Health
                  </Typography>
                  <div
                    style={{
                      marginTop: 8,
                      color:
                        positionDisplayInfo.status === 'active' && positionDisplayInfo.health !== undefined
                          ? getHealthColor(Number(positionDisplayInfo.health.mul(100).toFixed(0)), theme)
                          : '',
                    }}
                  >
                    {positionDisplayInfo.status === 'active' && positionDisplayInfo.health !== undefined
                      ? getHealthValue(positionDisplayInfo.health)
                      : '-'}
                  </div>
                </InfoItem>
                <InfoItem>
                  <Typography variant="description" color="secondary" fontWeight="medium">
                    APR
                  </Typography>
                  <div style={{ marginTop: 8 }}>
                    {getAprValue(isLoadingPositionMetrics, positionDisplayInfo.status, positionMetrics?.apr1h.apr)}
                  </div>
                </InfoItem>
                <InfoItem>
                  <Typography variant="description" color="secondary" fontWeight="medium">
                    PNL
                  </Typography>
                  <div style={{ marginTop: 8 }}>
                    {getPnlValue(isLoadingPositionMetrics, positionMetrics?.pnl.totalPnl)}
                  </div>
                </InfoItem>
                <InfoItem>
                  <Typography variant="description" color="secondary" fontWeight="medium">
                    In range
                  </Typography>
                  <div style={{ marginTop: 8 }}>{getInRangeValue(positionDisplayInfo.inRange, theme)}</div>
                </InfoItem>
              </InfoContainer>
              <SectionGap />
              {/* Pending rewards & fees */}
              {positionDisplayInfo.status === 'closed' ? (
                <PendingFeesRewardsTotalClosed />
              ) : (
                <PendingFeesRewardsTotal
                  positionInfo={positionInfo}
                  openClaimDialog={openClaimDialog}
                  positionStatus={positionDisplayInfo.status}
                />
              )}
              <SectionGap />
              {/* Price Range */}
              <TooltipSectionLabel variant="label2" color="secondary" fontWeight="medium">
                Price Range
                <Tooltip
                  content="The specified price interval for earning fees, only active when the pool price falls within it."
                  position="top"
                >
                  <IconInfo width={16} height={16} />
                </Tooltip>
              </TooltipSectionLabel>
              <CurrentPrice
                tokenXSymbol={isReversedPair ? configInfo.Y.displaySymbol || '' : configInfo.X.displaySymbol || ''}
                tokenYSymbol={isReversedPair ? configInfo.X.displaySymbol || '' : configInfo.Y.displaySymbol || ''}
                currentPrice={
                  isReversedPair
                    ? pool?.currentPrice().inverted().human.toDP(8).toString()
                    : pool?.currentPrice().human.toDP(8).toString()
                }
              />

              {/* <SectionGap /> */}
              {!isMobile && (
                <>
                  <MinMaxPriceLabelContainer>
                    <SectionLabel variant="body" color="secondary" style={{ marginBottom: 4 }}>
                      Min Price
                    </SectionLabel>
                    <SectionLabel variant="body" color="secondary" style={{ marginBottom: 4 }}>
                      Max Price
                    </SectionLabel>
                  </MinMaxPriceLabelContainer>
                  <MinMaxPriceLabelContainer>
                    <SectionContent style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
                        {getPositionRangeMin(configInfo.isReversedPair, positionDisplayInfo)}
                      </Typography>
                      <Typography color="secondary" variant="body">
                        {isReversedPair
                          ? `${configInfo.X.displaySymbol} per ${configInfo.Y.displaySymbol}`
                          : `${configInfo.Y.displaySymbol} per ${configInfo.X.displaySymbol}`}
                      </Typography>
                    </SectionContent>
                    <SectionContent style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
                        {getPositionRangeMax(configInfo.isReversedPair, positionDisplayInfo)}
                      </Typography>
                      <Typography color="secondary" variant="body">
                        {isReversedPair
                          ? `${configInfo.X.displaySymbol} per ${configInfo.Y.displaySymbol}`
                          : `${configInfo.Y.displaySymbol} per ${configInfo.X.displaySymbol}`}
                      </Typography>
                    </SectionContent>
                  </MinMaxPriceLabelContainer>
                </>
              )}
              {isMobile && (
                <>
                  <SectionLabel variant="body" color="secondary" style={{ marginBottom: 4 }}>
                    Min Price
                  </SectionLabel>
                  <SectionContent style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
                      {getPositionRangeMin(configInfo.isReversedPair, positionDisplayInfo)}
                    </Typography>
                    <Typography color="secondary" variant="body">
                      {isReversedPair
                        ? `${configInfo.X.displaySymbol} per ${configInfo.Y.displaySymbol}`
                        : `${configInfo.Y.displaySymbol} per ${configInfo.X.displaySymbol}`}
                    </Typography>
                  </SectionContent>
                  <SectionGap />
                  <SectionLabel variant="body" color="secondary" style={{ marginBottom: 4 }}>
                    Max Price
                  </SectionLabel>
                  <SectionContent style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
                      {getPositionRangeMax(configInfo.isReversedPair, positionDisplayInfo)}
                    </Typography>
                    <Typography color="secondary" variant="body">
                      {isReversedPair
                        ? `${configInfo.X.displaySymbol} per ${configInfo.Y.displaySymbol}`
                        : `${configInfo.Y.displaySymbol} per ${configInfo.X.displaySymbol}`}
                    </Typography>
                  </SectionContent>
                </>
              )}
              <SectionGap />
              {pool && (
                <div style={{ marginTop: 16 }}>
                  <LiquidityChartRangeInput
                    pool={pool}
                    reverseTokens={configInfo.isReversedPair}
                    ticksAtLimit={{
                      [Bound.LOWER]: false,
                      [Bound.UPPER]: false,
                    }}
                    tickA={positionDisplayInfo.position.getRange().tickA}
                    tickB={positionDisplayInfo.position.getRange().tickB}
                    onTickAChange={() => {}}
                    onTickBChange={() => {}}
                    interactive={false}
                  />
                </div>
              )}
              <SectionGap />
              <ButtonContainer>
                <Button
                  onClick={openDepositDialog}
                  disabled={
                    positionDisplayInfo.status === 'closed' ||
                    positionDisplayInfo.status === 'inactive' ||
                    pairInfo.frozenConfigInfos.some(
                      ci => ci.configId === positionDisplayInfo.position.configInfo.configId
                    ) ||
                    pairInfo.withdrawOnlyConfigs.some(
                      ci => ci.configId === positionDisplayInfo.position.configInfo.configId
                    )
                  }
                >
                  Deposit
                </Button>
                <Button outlined onClick={handleWithdrawClick} disabled={positionDisplayInfo.status === 'closed'}>
                  {positionDisplayInfo.status === 'inactive' ||
                  pairInfo.frozenConfigInfos.some(
                    ci => ci.configId === positionDisplayInfo.position.configInfo.configId
                  )
                    ? 'Close position'
                    : 'Withdraw'}
                </Button>
              </ButtonContainer>
            </ContentContainer>
          </LeftContainer>
          <RightContainer>
            <ExistingPositionBreakdown
              position={positionDisplayInfo}
              isReversedPair={configInfo.isReversedPair}
              supplyPoolX={supplyPoolX}
              supplyPoolY={supplyPoolY}
            />
          </RightContainer>
        </PositionOverviewContainer>
        {isOpenDepositModal && (
          <DepositDialog
            isOpen={isOpenDepositModal}
            onClose={closeDepositDialog}
            position={positionDisplayInfo.position}
            pool={pool}
            positionCapId={positionDisplayInfo.positionCapId}
            onDepositSuccess={onDepositSuccess}
            onDepositProcessed={onDepositProcessed}
            isReversedPair={configInfo.isReversedPair}
          />
        )}
        {isOpenWithdrawModal && reduceModalPosition && configInfo && (
          <WithdrawDialog
            isLoading={isLoadingWithdraw}
            isOpen={isOpenWithdrawModal}
            onClose={closeWithdrawDialog}
            positionInfo={reduceModalPosition}
            pool={pool}
            supplyPoolX={supplyPoolX}
            supplyPoolY={supplyPoolY}
            configInfo={configInfo}
            onWithdraw={onReduceConfirm}
            isReversedPair={configInfo.isReversedPair}
            isFrozenPosition={pairInfo.frozenConfigInfos.some(
              ci => ci.configId === positionDisplayInfo.position.configInfo.configId
            )}
          />
        )}
        {isOpenClaimDialog && (
          <ClaimDialog
            isLoadingClaim={isLoadingClaim}
            isLoadingCompound={isLoadingCompound}
            onClaim={onClaim}
            onCompound={onCompound}
            isOpen={isOpenClaimDialog}
            onClose={closeClaimDialog}
            configInfo={configInfo}
            isReversedPair={!!isReversedPair}
            positionInfo={positionInfo}
            rewardsFeesInfos={rewardsFeesInfos}
            isLoadingRewardsFees={isLoadingRewardsFees}
            rewardsFeesInfoError={rewardsFeesInfoError}
          />
        )}
      </>
    )
  )
}
